<template>
  <div
    class="pods-shell app-shell"
    :style="{
      marginTop: headerHeight + 'px',
      minHeight: 'calc(100vh - ' + headerHeight + 'px)'
    }"
  >
    <div class="pods-sidebar app-sidebar" :style="{ top: headerHeight + 'px' }">
      <el-menu
        class="pods-menu"
        :default-active="type"
        :default-openeds="['grp-chart-data', 'grp-live-data']"
        background-color="transparent"
        @select="selectType"
      >
        <el-submenu
          v-if="
            p('Pods.Capacitance Readings Chart.View') ||
              p('Pods.Connection Status Chart.View') ||
              p('Pods.Battery Levels Chart.View') ||
              p('Pods.Accelerometer Readings Chart.View') ||
              p('Pods.Temperature Chart.View') ||
              p('Pods.SPO2 Chart.View')
          "
          index="grp-chart-data"
        >
          <template slot="title">
            <i class="el-icon-data-line"></i>
            <span>Chart Data</span>
          </template>
          <el-menu-item
            v-if="p('Pods.Capacitance Readings Chart.View')"
            index="capacitance_readings"
          >
            Capacitance Readings
          </el-menu-item>
          <el-menu-item
            v-if="p('Pods.Connection Status Chart.View')"
            index="connection_status"
          >
            Connection Status
          </el-menu-item>
          <el-menu-item
            v-if="p('Pods.Battery Levels Chart.View')"
            index="battery_levels"
          >
            Battery Voltage
          </el-menu-item>
          <el-menu-item
            v-if="p('Pods.Accelerometer Readings Chart.View')"
            index="accelerometer_readings"
          >
            Accelerometer
          </el-menu-item>
          <el-menu-item
            v-if="p('Pods.Temperature Chart.View')"
            index="temperature"
          >
            Temperature
          </el-menu-item>
          <el-menu-item v-if="p('Pods.SPO2 Chart.View')" index="spo2">
            SPO2
          </el-menu-item>
        </el-submenu>

        <el-submenu v-if="p('Pods.Live Data.View')" index="grp-live-data">
          <template slot="title">
            <i class="el-icon-odometer"></i>
            <span>Live Data</span>
          </template>
          <el-menu-item index="live_data_view">Data View</el-menu-item>
          <el-menu-item index="live_data_chart">Chart View</el-menu-item>
        </el-submenu>

        <el-menu-item v-if="p('Pods.Calibration.View')" index="calibration">
          <i class="el-icon-aim"></i>
          <span>{{ $t('text.calibration') | firstLetterCapitalize }}</span>
        </el-menu-item>

        <el-menu-item v-if="p('Pods.Find Data.View')" index="find_data">
          <i class="el-icon-search"></i>
          <span>{{ $t('text.find_data') }}</span>
        </el-menu-item>

        <el-menu-item v-if="p('Pods.Diagnostics.View')" index="diagnostics">
          <i class="el-icon-cpu"></i>
          <span>Diagnostics</span>
        </el-menu-item>
      </el-menu>
    </div>

    <div class="pods-main app-main">
      <div class="pods-page">
        <AppBreadcrumb
          root="Pods"
          :current="typeTitle"
          @root-click="selectType(firstAvailableType)"
        >
          <el-button
            v-if="pageAction"
            type="primary"
            size="small"
            icon="el-icon-plus"
            @click="pageAction.onClick"
            >{{ pageAction.label }}</el-button
          >
        </AppBreadcrumb>

        <CapacitanceReadings
          v-if="type === 'capacitance_readings'"
          :tooltip="chartTooltip"
        />
        <ConnectionStatus
          v-if="type === 'connection_status'"
          :tooltip="chartTooltip"
        />
        <BatteryLevels
          v-if="type === 'battery_levels'"
          :tooltip="chartTooltip"
        />
        <AccelerometerReadings
          v-if="type === 'accelerometer_readings'"
          :tooltip="chartTooltip"
        />
        <Temperature v-if="type === 'temperature'" :tooltip="chartTooltip" />
        <Spo2 v-if="type === 'spo2'" />

        <LiveData
          v-if="type === 'live_data_view' || type === 'live_data_chart'"
          :activeView="type"
        />

        <Calibration v-if="type === 'calibration'" />
        <FindData v-if="type === 'find_data'" />
        <Diagnostics v-if="type === 'diagnostics'" />
      </div>
    </div>
  </div>
</template>

<script>
import CapacitanceReadings from '@/components/pods/chartData/CapacitanceReadings'
import ConnectionStatus from '@/components/pods/chartData/ConnectionStatus'
import BatteryLevels from '@/components/pods/chartData/BatteryLevels'
import AccelerometerReadings from '@/components/pods/chartData/AccelerometerReadings'
import Temperature from '@/components/pods/chartData/Temperature'
import Spo2 from '@/components/pods/chartData/Spo2'
import LiveData from '@/components/pods/LiveData'
import Calibration from '@/components/pods/Calibration'
import FindData from '@/components/pods/FindData'
import Diagnostics from '@/components/pods/Diagnostics'
import AppBreadcrumb from '@/components/AppBreadcrumb'
import headerHeight from '@/mixins/headerHeight'
import moment from 'moment-timezone'

const TYPE_TITLES = {
  capacitance_readings: 'Capacitance Readings',
  connection_status: 'Connection Status',
  battery_levels: 'Battery Voltage',
  accelerometer_readings: 'Accelerometer Readings',
  temperature: 'Temperature',
  spo2: 'SPO2',
  live_data_view: 'Live Data — Data View',
  live_data_chart: 'Live Data — Chart View',
  calibration: 'Calibration',
  find_data: 'Find Data',
  diagnostics: 'Diagnostics'
}

export default {
  mixins: [headerHeight],
  data() {
    return {
      type: 'capacitance_readings',
      pageAction: null,
      chartTooltip: {
        trigger: 'axis',
        formatter(params) {
          let tooltipStr = `<div>${moment(params[0].axisValue).format(
            'DD/MM/YY HH:mm:ss'
          )}</div>`
          params.forEach(item => {
            if (tooltipStr.indexOf(item.color) < 0) {
              tooltipStr =
                tooltipStr +
                `<div><div style="display:inline-block; margin-right: 5px; width: 10px; height: 10px; border-radius: 5px; background-color:${
                  item.color
                }"></div>${item.seriesName} : ${
                  item.value[item.seriesIndex + 1]
                }</div>`
            }
          })
          return tooltipStr
        }
      }
    }
  },
  components: {
    CapacitanceReadings,
    ConnectionStatus,
    BatteryLevels,
    AccelerometerReadings,
    Temperature,
    Spo2,
    LiveData,
    Calibration,
    FindData,
    Diagnostics,
    AppBreadcrumb
  },
  computed: {
    typeTitle() {
      return TYPE_TITLES[this.type] || ''
    },
    firstAvailableType() {
      if (this.$permission('Pods.Capacitance Readings Chart.View'))
        return 'capacitance_readings'
      if (this.$permission('Pods.Connection Status Chart.View'))
        return 'connection_status'
      if (this.$permission('Pods.Battery Levels Chart.View'))
        return 'battery_levels'
      if (this.$permission('Pods.Accelerometer Readings Chart.View'))
        return 'accelerometer_readings'
      if (this.$permission('Pods.Temperature Chart.View')) return 'temperature'
      if (this.$permission('Pods.SPO2 Chart.View')) return 'spo2'
      if (this.$permission('Pods.Live Data.View')) return 'live_data_view'
      if (this.$permission('Pods.Calibration.View')) return 'calibration'
      if (this.$permission('Pods.Find Data.View')) return 'find_data'
      return 'diagnostics'
    }
  },
  mounted() {
    this.type = this.firstAvailableType
  },
  methods: {
    selectType(type) {
      this.type = type
      this.pageAction = null
    },
    setPageAction(action) {
      this.pageAction = action
    },
    clearPageAction() {
      this.pageAction = null
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';

.pods-page {
  flex: 1;
  padding: 20px 24px;

  /deep/ .page-title {
    display: none;
  }
}
</style>
